// src/pages/RenewalBatchPage.js — batch detail for Excel-driven renewals
// [marker: renewals-tracker v1] tracker: paid / no response / not sent, "not paid by" date, follow-up emails, mark paid
import React, { useState, useEffect, useRef } from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { useParams, Link, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';

export default function RenewalBatchPage() {
  const { id } = useParams();
  const { isManagerOrAbove } = useAuth();
  const [data, setData] = useState(null);
  const [preview, setPreview] = useState(null);
  const [sending, setSending] = useState(false);
  const [confirmSend, setConfirmSend] = useState(false);
  const [replacingLetterhead, setReplacingLetterhead] = useState(false);
  const [uploadingLhPdf, setUploadingLhPdf] = useState(false); /* [marker: letterhead-bg-fix v1] */
  const [track, setTrack] = useState('all');       // [marker: renewals-tracker v1]
  const [paidBy, setPaidBy] = useState('');
  const [followUp, setFollowUp] = useState(false);
  const navigate = useNavigate();

  const load = () => {
    api.get(`/renewals/batches/${id}`)
      .then(res => setData(res.data))
      .catch(() => toast.error('Could not load batch.'));
  };
  useEffect(load, [id]);

  if (!data) return <div className="loading-page"><div className="spinner" /></div>;
  const { batch, items, hasLetterheadPdf } = data;
  const canEdit = isManagerOrAbove() && batch.status !== 'sent' && batch.status !== 'sending';

  const stats = {
    total:    items.length,
    excluded: items.filter(i => i.excluded).length,
    withPdf:  items.filter(i => i.pdf_filename && !i.excluded).length,
    sent:     items.filter(i => i.email_status === 'sent').length,
    failed:   items.filter(i => i.email_status === 'failed').length,
  };
  // [marker: renewals-tracker v1]
  const paidInTime = i => !!i.paid_on && (!paidBy || i.paid_on <= paidBy);
  const groupOf = i => {
    if (i.excluded) return 'excluded';
    if (paidInTime(i)) return 'paid';
    if (i.email_status === 'sent') return 'no_response';
    return 'not_sent';
  };
  const live = items.filter(i => !i.excluded);
  const tcount = {
    all: live.length,
    paid: live.filter(i => groupOf(i) === 'paid').length,
    unpaid: live.filter(i => groupOf(i) !== 'paid').length,
    no_response: live.filter(i => groupOf(i) === 'no_response').length,
    not_sent: live.filter(i => groupOf(i) === 'not_sent').length,
  };
  const shown = track === 'all' ? items
    : track === 'unpaid' ? live.filter(i => groupOf(i) !== 'paid')
    : live.filter(i => groupOf(i) === track);
  const canFollowUp = isManagerOrAbove() && track !== 'all' && track !== 'paid' && shown.some(i => i.recipient_email);
  const readyToSend = stats.withPdf > 0 && !!batch.letterhead_filename;

  const handleSendBatch = async () => {
    setSending(true);
    setConfirmSend(false);
    try {
      const res = await api.post(`/renewals/batches/${id}/send`);
      toast.success(`Sent ${res.data.sent}. Failed ${res.data.failed}.`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Send failed.');
    } finally {
      setSending(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete batch "${batch.name}"? Uploaded files will be removed. This cannot be undone.`)) return;
    try {
      await api.delete(`/renewals/batches/${id}`);
      toast.success('Batch deleted.');
      navigate('/renewals');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not delete.');
    }
  };

  const handleReplaceLetterhead = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.docx')) {
      toast.error('Letterhead must be a .docx file.');
      return;
    }
    setReplacingLetterhead(true);
    const fd = new FormData();
    fd.append('letterhead', file);
    try {
      await api.post(`/renewals/batches/${id}/letterhead`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Letterhead replaced.');
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Upload failed.');
    } finally {
      setReplacingLetterhead(false);
    }
  };

  // [marker: letterhead-bg-fix v1]
  // Bulk letters composite a PDF copy of the letterhead behind each page. Word
  // stores the letterhead as an EMF vector that LibreOffice renders blank, so
  // the server can't derive it from the .docx — a Word-exported PDF is required.
  const handleUploadLetterheadPdf = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Letterhead background must be a .pdf file.');
      return;
    }
    setUploadingLhPdf(true);
    const fd = new FormData();
    fd.append('letterheadPdf', file);
    try {
      await api.post(`/renewals/batches/${id}/letterhead-pdf`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Letterhead PDF uploaded — bulk letters will use it.');
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Upload failed.');
    } finally {
      setUploadingLhPdf(false);
    }
  };

  return (
    <div>
      <Link to="/renewals" style={{ fontSize: 14, color: 'var(--gray-600)', textDecoration: 'none' }}>← Back to renewals</Link>

      <div className="page-header" style={{ marginTop: 8 }}>
        <div>
          <h1 className="page-title">{batch.name}</h1>
          <p className="page-subtitle">
            {[batch.renewal_month, batch.renewal_year].filter(Boolean).join(' ')}
            {batch.source_excel_filename ? ` · Source: ${batch.source_excel_filename}` : ''}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {canEdit && batch.status === 'draft' && (
            <button className="btn btn-secondary" onClick={handleDelete}>Delete Batch</button>
          )}
          {canEdit && readyToSend && (
            <button
              className="btn btn-primary"
              onClick={() => setConfirmSend(true)}
              disabled={sending}
              style={{ minWidth: 140 }}
            >
              {sending ? 'Sending…' : `Send to ${stats.withPdf}`}
            </button>
          )}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <Stat label="Total" value={stats.total} />
        <Stat label="With PDF" value={stats.withPdf} color={stats.withPdf === stats.total - stats.excluded ? '#15803d' : undefined} />
        <Stat label="Excluded" value={stats.excluded} />
        <Stat label="Sent" value={stats.sent} color="#15803d" />
        <Stat label="Paid" value={tcount.paid} color="#15803d" />
        <Stat label="Not paid" value={tcount.unpaid} color={tcount.unpaid ? '#92400e' : undefined} />
        {stats.failed > 0 && <Stat label="Failed" value={stats.failed} color="#991b1b" />}
      </div>

      <LetterheadCard
        filename={batch.letterhead_filename}
        canEdit={canEdit}
        onReplace={handleReplaceLetterhead}
        loading={replacingLetterhead}
      />

      <LetterheadPdfCard
        present={!!hasLetterheadPdf}
        canEdit={canEdit}
        onUpload={handleUploadLetterheadPdf}
        loading={uploadingLhPdf}
      />

      <BulkDocumentsCard
        batchId={id}
        hasLetterhead={!!batch.letterhead_filename}
        stats={stats}
      />

      {/* [marker: renewals-tracker v1] tracker filters */}
      <div className="card" style={{ marginBottom: 12 }}>
        <div className="card-body" style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', padding: 12 }}>
          {[
            ['all', 'Everyone'], ['unpaid', 'Not paid'], ['no_response', 'Sent, no response'],
            ['not_sent', 'Not sent yet'], ['paid', 'Paid'],
          ].map(([k, label]) => (
            <button key={k} type="button" onClick={() => setTrack(k)}
              className={track === k ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm'}>
              {label} ({tcount[k]})
            </button>
          ))}
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.85rem', marginLeft: 'auto' }}>
            Paid by
            <input type="date" value={paidBy} onChange={e => setPaidBy(e.target.value)} style={{ ...input, width: 'auto', padding: '4px 8px' }} />
            {paidBy && <button type="button" className="btn btn-ghost btn-sm" onClick={() => setPaidBy('')}>Clear</button>}
          </label>
          {isManagerOrAbove() && (
            <button type="button" className="btn btn-primary btn-sm" disabled={!canFollowUp}
              title={canFollowUp ? '' : 'Pick a group first: Not paid, Sent no response, or Not sent yet'}
              onClick={() => setFollowUp(true)}>
              ✉ Email this group ({shown.filter(i => !i.excluded && i.recipient_email).length})
            </button>
          )}
        </div>
        {paidBy && (
          <div style={{ padding: '0 12px 10px', fontSize: '0.8rem', color: 'var(--gray-600)' }}>
            Counting only payments made on or before {paidBy}. Anyone who paid later counts as not paid.
          </div>
        )}
      </div>

      <div className="card">
        {items.length === 0 ? (
          <div className="card-body" style={{ textAlign: 'center', padding: 48, color: 'var(--gray-500)' }}>
            No recipients in this batch.
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--gray-50)', borderBottom: '1px solid var(--gray-200)' }}>
                <th style={th}>Recipient</th>
                <th style={th}>Organization</th>
                <th style={th}>Subject</th>
                <th style={th}>PDF</th>
                <th style={th}>Status</th>
                <th style={th}>Payment</th>
                <th style={{ ...th, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {shown.length === 0 && (
                <tr><td colSpan={7} style={{ ...td, textAlign: 'center', color: 'var(--gray-500)' }}>Nobody in this group.</td></tr>
              )}
              {shown.map(item => (
                <ItemRow
                  key={item.id}
                  item={item}
                  batchId={id}
                  canEdit={canEdit}
                  canMark={isManagerOrAbove()}
                  paidInTime={paidInTime(item)}
                  onChange={load}
                  onPreview={() => setPreview({ itemId: item.id })}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>

      {preview && (
        <PreviewModal
          batchId={id}
          itemId={preview.itemId}
          onClose={() => setPreview(null)}
          canEdit={canEdit}
          onSent={load}
        />
      )}

      {followUp && (
        <FollowUpModal
          batchId={id}
          group={track}
          paidBy={paidBy}
          people={shown.filter(i => !i.excluded && i.recipient_email)}
          onClose={() => setFollowUp(false)}
          onSent={() => { setFollowUp(false); load(); }}
        />
      )}

      {confirmSend && (
        <ConfirmSendModal
          stats={stats}
          batch={batch}
          onCancel={() => setConfirmSend(false)}
          onConfirm={handleSendBatch}
        />
      )}
    </div>
  );
}

function LetterheadCard({ filename, canEdit, onReplace, loading }) {
  const fileRef = useRef(null);
  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div className="card-body" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <div style={{ fontWeight: 600, fontSize: '0.9rem', marginBottom: 2 }}>Letterhead</div>
          <div style={{ fontSize: '0.85rem', color: filename ? 'var(--gray-700)' : '#991b1b' }}>
            {filename ? `✓ ${filename} uploaded` : '⚠ No letterhead — required before sending'}
          </div>
        </div>
        {canEdit && (
          <>
            <input
              ref={fileRef}
              type="file"
              accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              onChange={e => onReplace(e.target.files[0])}
              style={{ display: 'none' }}
            />
            <button
              className="btn btn-sm btn-secondary"
              onClick={() => fileRef.current?.click()}
              disabled={loading}
            >
              {loading ? 'Uploading…' : (filename ? 'Replace Letterhead' : 'Upload Letterhead')}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// [marker: letterhead-bg-fix v1]
// Bulk "All Letters" needs a PDF copy of the letterhead: Word keeps it as an
// EMF vector that LibreOffice renders blank, and the leftover raster inside the
// .docx is often an OLD design — which is what used to get stamped on every
// bulk letter. Without this PDF, bulk generation now refuses rather than
// silently printing the wrong branding. Individual letters are unaffected.
function LetterheadPdfCard({ present, canEdit, onUpload, loading }) {
  const fileRef = useRef(null);
  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div className="card-body" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
        <div>
          <div style={{ fontWeight: 600, fontSize: '0.9rem', marginBottom: 2 }}>
            Letterhead PDF <span style={{ fontWeight: 400, color: 'var(--gray-500)' }}>(for bulk printing)</span>
          </div>
          <div style={{ fontSize: '0.85rem', color: present ? 'var(--gray-700)' : '#991b1b' }}>
            {present
              ? '✓ letterhead-bg.pdf uploaded — bulk letters will print on this letterhead'
              : '⚠ Required for "All Letters". In Word: open your letterhead → Save As → PDF → upload it here.'}
          </div>
          {!present && (
            <div style={{ fontSize: '0.78rem', color: 'var(--gray-500)', marginTop: 4, maxWidth: 620, lineHeight: 1.45 }}>
              The .docx letterhead can't be rendered faithfully on the server (Word stores it as a
              vector that LibreOffice reads as blank), so bulk letters need a PDF copy.
              Individual letter downloads work without it.
            </div>
          )}
        </div>
        {canEdit && (
          <>
            <input
              ref={fileRef}
              type="file"
              accept=".pdf,application/pdf"
              onChange={e => onUpload(e.target.files[0])}
              style={{ display: 'none' }}
            />
            <button
              className="btn btn-sm btn-secondary"
              onClick={() => fileRef.current?.click()}
              disabled={loading}
              style={{ flexShrink: 0 }}
            >
              {loading ? 'Uploading…' : (present ? 'Replace Letterhead PDF' : 'Upload Letterhead PDF')}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function BulkDocumentsCard({ batchId, hasLetterhead, stats }) {
  const activeCount = stats.total - stats.excluded;
  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div className="card-header">
        <span className="card-title">Bulk Print Documents</span>
      </div>
      <div className="card-body">
        <div style={{ fontSize: '0.85rem', color: 'var(--gray-600)', marginBottom: 12 }}>
          Combined PDFs for printing and physical mailing.
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <BulkDownloadButton
            label="All Letters"
            icon="📄"
            path={`/renewals/batches/${batchId}/letters.pdf`}
            filename={`Letters-Batch-${batchId}.pdf`}
            disabled={!hasLetterhead || activeCount === 0}
            disabledReason={!hasLetterhead ? 'Upload a letterhead first' : 'No active recipients'}
            slow
          />
          <BulkDownloadButton
            label="All Statements"
            icon="📑"
            path={`/renewals/batches/${batchId}/statements.pdf`}
            filename={`Statements-Batch-${batchId}.pdf`}
            disabled={stats.withPdf === 0}
            disabledReason="No statement PDFs uploaded yet"
          />
          <BulkDownloadButton
            label="All Envelopes"
            icon="✉️"
            path={`/renewals/batches/${batchId}/envelopes.pdf`}
            filename={`Envelopes-Batch-${batchId}.pdf`}
            disabled={activeCount === 0}
            disabledReason="No active recipients"
          />
        </div>
        <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)', marginTop: 10 }}>
          Letters PDF can take a few minutes for large batches (LibreOffice converts each document).
          Statements and envelopes generate instantly.
        </div>
      </div>
    </div>
  );
}

function BulkDownloadButton({ label, icon, path, filename, disabled, disabledReason, slow }) {
  const [loading, setLoading] = useState(false);

  const handleClick = async () => {
    setLoading(true);
    try {
      const response = await api.get(path, {
        responseType: 'blob',
        timeout: 30 * 60 * 1000,  // 30 minutes for large letter batches
      });
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${filename}`);
    } catch (err) {
      let errMsg = 'Download failed';
      if (err.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const parsed = JSON.parse(text);
          errMsg = parsed.error || errMsg;
        } catch {}
      } else if (err.message) {
        errMsg = err.message;
      }
      toast.error(errMsg, { duration: 6000 });
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      className="btn btn-secondary"
      onClick={handleClick}
      disabled={disabled || loading}
      title={disabled ? disabledReason : (slow ? 'May take several minutes for large batches' : '')}
      style={{ minWidth: 200, position: 'relative' }}
    >
      {loading
        ? (slow ? '⏳ Generating… (please wait)' : 'Generating…')
        : `${icon} ${label}`}
    </button>
  );
}

function ItemRow({ item, batchId, canEdit, canMark, paidInTime, onChange, onPreview }) {
  const [editingSubject, setEditingSubject] = useState(false);
  const [subjectValue, setSubjectValue] = useState(item.subject_override || '');
  const fileInput = useRef(null);
  const [uploading, setUploading] = useState(false);

  const recipientName = [item.recipient_first_name, item.recipient_last_name].filter(Boolean).join(' ') || '—';

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.type !== 'application/pdf') {
      toast.error('Only PDFs accepted.');
      return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append('pdf', file);
    try {
      await api.post(`/renewals/batches/${batchId}/items/${item.id}/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`PDF uploaded`);
      onChange();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Upload failed.');
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  };

  const handleRemovePdf = async () => {
    if (!window.confirm(`Remove PDF for ${recipientName}?`)) return;
    try {
      await api.delete(`/renewals/batches/${batchId}/items/${item.id}/pdf`);
      onChange();
    } catch (err) { toast.error('Could not remove.'); }
  };

  const handleSaveSubject = async () => {
    try {
      await api.patch(`/renewals/batches/${batchId}/items/${item.id}`, {
        subjectOverride: subjectValue.trim() || null,
      });
      setEditingSubject(false);
      onChange();
    } catch (err) { toast.error('Could not save.'); }
  };

  const handleToggleExclude = async () => {
    try {
      await api.patch(`/renewals/batches/${batchId}/items/${item.id}`, { excluded: !item.excluded });
      onChange();
    } catch (err) { toast.error('Could not update.'); }
  };

  // [marker: renewals-tracker v1]
  const handleMarkPaid = async (on) => {
    try {
      await api.patch(`/renewals/batches/${batchId}/items/${item.id}`, { markedPaid: on ? new Date().toISOString().slice(0, 10) : null });
      onChange();
    } catch (err) { toast.error('Could not update.'); }
  };

  const handleResend = async () => {
    try {
      await api.post(`/renewals/batches/${batchId}/items/${item.id}/send`);
      toast.success('Resent.');
      onChange();
    } catch (err) { toast.error(err.response?.data?.error || 'Send failed.'); }
  };

  const rowStyle = {
    borderBottom: '1px solid var(--gray-100)',
    opacity: item.excluded ? 0.5 : 1,
    background: item.email_status === 'failed' ? '#fef2f2' : 'transparent',
  };

  return (
    <tr style={rowStyle}>
      <td style={td}>
        <div style={{ fontWeight: 600 }}>{recipientName}</div>
        <div style={{ fontSize: '0.8rem', color: 'var(--gray-600)' }}>{item.recipient_email}</div>
      </td>
      <td style={td}>
        <div style={{ fontSize: '0.85rem' }}>{item.recipient_business_name || <span style={{ color: 'var(--gray-400)' }}>—</span>}</div>
        {item.recipient_title && (
          <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>{item.recipient_title}</div>
        )}
      </td>
      <td style={{ ...td, maxWidth: 220 }}>
        {editingSubject ? (
          <div style={{ display: 'flex', gap: 4 }}>
            <input type="text" value={subjectValue} onChange={e => setSubjectValue(e.target.value)} placeholder="(use template)"
                   style={{ ...input, fontSize: '0.85rem', padding: '4px 8px' }} autoFocus />
            <button className="btn btn-sm btn-primary" onClick={handleSaveSubject}>OK</button>
          </div>
        ) : (
          <div style={{ fontSize: '0.85rem', color: item.subject_override ? 'var(--gray-800)' : 'var(--gray-500)', fontStyle: item.subject_override ? 'normal' : 'italic' }}>
            {item.subject_override || '(default template)'}
            {canEdit && (
              <button onClick={() => { setSubjectValue(item.subject_override || ''); setEditingSubject(true); }}
                      style={editBtn} title="Override subject">✏</button>
            )}
          </div>
        )}
      </td>
      <td style={td}>
        {item.pdf_filename ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ color: '#15803d', fontWeight: 600, fontSize: '0.85rem' }}>✓ Uploaded</span>
            {canEdit && (
              <button onClick={handleRemovePdf} style={smallLinkBtn} title="Remove PDF">×</button>
            )}
          </div>
        ) : canEdit ? (
          <>
            <input ref={fileInput} type="file" accept="application/pdf" onChange={handleUpload} style={{ display: 'none' }} />
            <button className="btn btn-sm btn-secondary" onClick={() => fileInput.current?.click()} disabled={uploading}>
              {uploading ? 'Uploading…' : 'Upload PDF'}
            </button>
          </>
        ) : (
          <span style={{ color: 'var(--gray-400)', fontSize: '0.85rem' }}>No PDF</span>
        )}
      </td>
      <td style={td}><ItemStatus item={item} /></td>
      <td style={td}>
        {item.paid_on ? (
          <div style={{ fontSize: '0.8rem' }}>
            <span style={{ color: paidInTime ? '#15803d' : '#92400e', fontWeight: 600 }}>✓ Paid {fmtDay(item.paid_on)}</span>
            {item.marked_paid_at && canMark && (
              <button onClick={() => handleMarkPaid(false)} style={{ ...markBtn, marginLeft: 6 }} title="Undo 'marked paid'">undo</button>
            )}
          </div>
        ) : item.excluded ? null : (
          <div style={{ fontSize: '0.8rem' }}>
            <span style={{ color: 'var(--gray-500)' }}>Not paid</span>
            {canMark && <button onClick={() => handleMarkPaid(true)} style={{ ...markBtn, marginLeft: 6 }} title="Paid by check or another way the app didn't see">Mark paid</button>}
          </div>
        )}
        {item.followup_count > 0 && (
          <div style={{ fontSize: '0.72rem', color: 'var(--gray-500)' }}>
            {item.followup_count} reminder{item.followup_count === 1 ? '' : 's'} · last {fmtDay(item.last_followup_at)}
          </div>
        )}
      </td>
      <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
        <button className="btn btn-sm btn-secondary" onClick={onPreview} style={{ marginRight: 4 }}>Preview</button>
        {canEdit && item.email_status === 'failed' && item.pdf_filename && (
          <button className="btn btn-sm btn-primary" onClick={handleResend} style={{ marginRight: 4 }}>Retry</button>
        )}
        {canEdit && (
          <button className="btn btn-sm btn-secondary" onClick={handleToggleExclude}>
            {item.excluded ? 'Include' : 'Exclude'}
          </button>
        )}
      </td>
    </tr>
  );
}

// [marker: renewals-tracker v1]
function fmtDay(v) {
  const m = String(v || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? new Date(+m[1], +m[2] - 1, +m[3]).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';
}

const GROUP_LABEL = { unpaid: 'not paid', no_response: 'sent, no response', not_sent: 'not sent yet' };

function FollowUpModal({ batchId, group, paidBy, people, onClose, onSent }) {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [attach, setAttach] = useState(true);
  const [saveDefault, setSaveDefault] = useState(false);
  const [sending, setSending] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get('/renewals/templates').then(res => {
      setSubject(res.data.followupSubject || '');
      setBody(res.data.followupBody || '');
    }).catch(() => {}).finally(() => setLoaded(true));
  }, []);

  const send = async () => {
    if (!subject.trim() || !body.replace(/<[^>]*>/g, '').trim()) return toast.error('Add a subject and a message.');
    if (!window.confirm(`Email ${people.length} ${people.length === 1 ? 'member' : 'members'} now?`)) return;
    setSending(true);
    try {
      const res = await api.post(`/renewals/batches/${batchId}/follow-up`, {
        group, paidBy: paidBy || null, subject, body, attachStatement: attach, saveAsDefault: saveDefault,
        itemIds: people.map(p => p.id),
      });
      if (res.data.failed) {
        toast.error(`Sent ${res.data.sent}. ${res.data.failed} could not be sent: ${(res.data.failures || []).map(f => f.name).slice(0, 3).join(', ')}`, { duration: 8000 });
      } else {
        toast.success(`Reminder sent to ${res.data.sent}.`);
      }
      onSent();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not send.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div style={overlayStyle} onClick={sending ? undefined : onClose}>
      <div style={{ background: '#fff', borderRadius: 8, width: '100%', maxWidth: 720, maxHeight: '92vh', overflow: 'auto' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 18px', borderBottom: '1px solid var(--gray-200)' }}>
          <strong>Email {people.length} {people.length === 1 ? 'member' : 'members'} ({GROUP_LABEL[group] || group}{paidBy ? ` by ${paidBy}` : ''})</strong>
          <button onClick={onClose} disabled={sending} style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: 'var(--gray-500)' }}>×</button>
        </div>
        <div style={{ padding: 18 }}>
          <div style={{ fontSize: '0.8rem', color: 'var(--gray-600)', marginBottom: 10, maxHeight: 70, overflowY: 'auto' }}>
            To: {people.map(p => p.recipient_business_name || p.recipient_email).join(', ')}
          </div>
          {!loaded ? <div className="spinner" /> : (
            <>
              <label style={{ display: 'block', fontWeight: 600, fontSize: '0.85rem', marginBottom: 4 }}>Subject</label>
              <input type="text" value={subject} onChange={e => setSubject(e.target.value)} style={{ ...input, marginBottom: 12 }} />
              <label style={{ display: 'block', fontWeight: 600, fontSize: '0.85rem', marginBottom: 4 }}>Message</label>
              <ReactQuill theme="snow" value={body} onChange={setBody} style={{ minHeight: 200, marginBottom: 8 }} />
              <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)', marginBottom: 10 }}>
                Merge fields like {'{{salutation_lastname}}'}, {'{{business_name}}'}, {'{{renewal_month}}'} are filled in for each member.
              </div>
              <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: '0.85rem', marginBottom: 6 }}>
                <input type="checkbox" checked={attach} onChange={e => setAttach(e.target.checked)} /> Attach their membership statement (PDF)
              </label>
              <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: '0.85rem' }}>
                <input type="checkbox" checked={saveDefault} onChange={e => setSaveDefault(e.target.checked)} /> Save this wording as the default reminder
              </label>
            </>
          )}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '12px 18px', borderTop: '1px solid var(--gray-200)' }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={sending}>Cancel</button>
          <button className="btn btn-primary" onClick={send} disabled={sending || !loaded}>{sending ? 'Sending…' : `Send to ${people.length}`}</button>
        </div>
      </div>
    </div>
  );
}

const overlayStyle = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 };

function ItemStatus({ item }) {
  if (item.excluded) return <span style={{ fontSize: '0.75rem', color: 'var(--gray-500)', fontStyle: 'italic' }}>Excluded</span>;
  if (item.email_status === 'sent') return <span style={{ fontSize: '0.75rem', color: '#15803d', fontWeight: 600 }}>✓ Sent</span>;
  if (item.email_status === 'failed') {
    return <span style={{ fontSize: '0.75rem', color: '#991b1b', fontWeight: 600 }} title={item.email_error || ''}>✗ Failed</span>;
  }
  if (item.pdf_filename) return <span style={{ fontSize: '0.75rem', color: 'var(--gray-700)', fontWeight: 600 }}>Ready</span>;
  return <span style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>Awaiting PDF</span>;
}

function PreviewModal({ batchId, itemId, onClose, canEdit, onSent }) {
  const [preview, setPreview] = useState(null);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get(`/renewals/batches/${batchId}/items/${itemId}/preview`)
      .then(res => setPreview(res.data))
      .catch(() => toast.error('Could not load preview.'));
  }, [batchId, itemId]);

  const handleSend = async () => {
    setSending(true);
    try {
      await api.post(`/renewals/batches/${batchId}/items/${itemId}/send`);
      toast.success('Sent.');
      onSent();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Send failed.');
    } finally {
      setSending(false);
    }
  };

  const downloadLetter = async () => {
    try {
      const res = await api.get(`/renewals/batches/${batchId}/items/${itemId}/letter`, { responseType: 'blob' });
      const cd = res.headers['content-disposition'] || '';
      const m = cd.match(/filename="?([^"]+)"?/);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = m ? m[1] : 'Renewal-Letter.docx';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      let msg = 'Could not download letter.';
      if (err.response?.data instanceof Blob) {
        try { msg = JSON.parse(await err.response.data.text()).error || msg; } catch {}
      }
      toast.error(msg);
    }
  };
  const viewPdf = async () => {
    try {
      const res = await api.get(`/renewals/batches/${batchId}/items/${itemId}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      let msg = 'Could not open PDF.';
      if (err.response?.data instanceof Blob) {
        try { msg = JSON.parse(await err.response.data.text()).error || msg; } catch {}
      }
      toast.error(msg);
    }
  };

  return (
    <div style={overlay} onClick={onClose}>
      <div style={{ ...modal, maxWidth: 720 }} onClick={e => e.stopPropagation()}>
        <div style={modalHeader}>
          <h3 style={{ margin: 0 }}>Preview</h3>
          <button onClick={onClose} style={closeBtn}>×</button>
        </div>
        <div style={{ padding: 20 }}>
          {!preview ? (
            <div style={{ textAlign: 'center', padding: 24 }}>Loading…</div>
          ) : (
            <>
              <PreviewField label="To" value={preview.recipient} />
              <PreviewField label="Subject" value={preview.emailSubject} />
              <div style={previewBox}>
                <div style={previewLabel}>Email Body</div>
                <div style={{ padding: 12, background: '#fff', border: '1px solid var(--gray-200)', borderRadius: 4 }}
                     dangerouslySetInnerHTML={{ __html: preview.emailBody }} />
              </div>
              <div style={previewBox}>
                <div style={previewLabel}>Attachments</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {preview.hasLetterhead ? (
                    <button onClick={downloadLetter} style={attachBtn}>
                      📄 Download Letter (.docx — letterhead merged with this recipient)
                    </button>
                  ) : (
                    <div style={{ fontSize: '0.85rem', color: '#991b1b', padding: 8 }}>
                      ⚠ No letterhead uploaded for this batch.
                    </div>
                  )}
                  {preview.hasPdf ? (
                    <button onClick={viewPdf} style={attachBtn}>
                      📎 View Uploaded Statement PDF
                    </button>
                  ) : (
                    <div style={{ fontSize: '0.85rem', color: '#991b1b', padding: 8 }}>
                      ⚠ No PDF uploaded for this recipient.
                    </div>
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20, paddingTop: 16, borderTop: '1px solid var(--gray-200)' }}>
                <button className="btn btn-secondary" onClick={onClose}>Close</button>
                {canEdit && preview.hasPdf && preview.hasLetterhead && !preview.excluded && preview.emailStatus !== 'sent' && (
                  <button className="btn btn-primary" onClick={handleSend} disabled={sending}>
                    {sending ? 'Sending…' : 'Send This One Now'}
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function PreviewField({ label, value }) {
  return (
    <div style={previewBox}>
      <div style={previewLabel}>{label}</div>
      <div style={{ fontSize: '0.9rem' }}>{value}</div>
    </div>
  );
}

function ConfirmSendModal({ stats, batch, onCancel, onConfirm }) {
  return (
    <div style={overlay} onClick={onCancel}>
      <div style={modal} onClick={e => e.stopPropagation()}>
        <div style={modalHeader}>
          <h3 style={{ margin: 0 }}>Send Batch?</h3>
          <button onClick={onCancel} style={closeBtn}>×</button>
        </div>
        <div style={{ padding: 20 }}>
          <p>Personalized email with attachments will go to <strong>{stats.withPdf}</strong> recipients.</p>
          {stats.excluded > 0 && <p style={{ color: 'var(--gray-600)' }}>{stats.excluded} excluded.</p>}
          {stats.total - stats.withPdf - stats.excluded > 0 && (
            <p style={{ color: '#92400e', background: '#fef3c7', padding: 10, borderRadius: 6 }}>
              ⚠ {stats.total - stats.withPdf - stats.excluded} recipients are missing PDFs and will be skipped.
              You can upload theirs later and resend individually.
            </p>
          )}
          <p style={{ fontSize: '0.85rem', color: 'var(--gray-600)' }}>
            Each email goes out one at a time. This cannot be undone.
          </p>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
            <button className="btn btn-secondary" onClick={onCancel}>Cancel</button>
            <button className="btn btn-primary" onClick={onConfirm}>Send Batch</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div style={{ textAlign: 'center', padding: '12px 18px', background: '#fff', border: '1px solid var(--gray-200)', borderRadius: 6, minWidth: 90 }}>
      <div style={{ fontSize: '1.5rem', fontWeight: 700, color: color || 'var(--gray-900)' }}>{value}</div>
      <div style={{ fontSize: '0.75rem', color: 'var(--gray-600)', textTransform: 'uppercase', letterSpacing: 0.3 }}>{label}</div>
    </div>
  );
}

const th = { textAlign: 'left', padding: '12px 14px', fontSize: '0.8rem', fontWeight: 600, color: 'var(--gray-600)', textTransform: 'uppercase', letterSpacing: 0.3 };
const td = { padding: '10px 14px', fontSize: '0.9rem', color: 'var(--gray-800)', verticalAlign: 'middle' };
const input = { width: '100%', padding: '8px 12px', fontSize: '0.95rem', border: '1px solid var(--gray-300)', borderRadius: 6, background: '#fff', boxSizing: 'border-box' };
const editBtn = { marginLeft: 4, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gray-500)', fontSize: 12 };
const markBtn = { background: 'none', border: 'none', color: '#1d4ed8', cursor: 'pointer', fontSize: '0.78rem', padding: 0, textDecoration: 'underline' }; // [marker: renewals-tracker v1]
const smallLinkBtn = { background: 'none', border: 'none', color: '#991b1b', cursor: 'pointer', fontSize: 16, padding: 0 };
const overlay = { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 };
const modal = { background: '#fff', borderRadius: 8, width: '100%', maxWidth: 520, maxHeight: '90vh', overflow: 'auto', boxShadow: '0 10px 40px rgba(0,0,0,0.2)' };
const modalHeader = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid var(--gray-200)' };
const closeBtn = { background: 'none', border: 'none', fontSize: 28, cursor: 'pointer', color: 'var(--gray-500)', padding: 0, lineHeight: 1 };
const previewBox = { marginBottom: 14 };
const previewLabel = { fontSize: '0.75rem', fontWeight: 700, color: 'var(--gray-600)', textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 4 };
const attachBtn = { padding: '10px 14px', textAlign: 'left', background: 'var(--gray-50)', border: '1px solid var(--gray-200)', borderRadius: 6, cursor: 'pointer', fontSize: '0.9rem', color: 'var(--gray-800)' };
